/**
 * 月次 MCS の表示判定（課題 #61 / #44 の確定仕様）。
 *
 * #44 で「正は mcs_mode（segment 300 件 / 音声 14 件）とし、録音日数では判定しない」と確定した。
 * それ以前の実装は `data_volume_flag * 28` で録音日数を逆算し、`14 - 日数` を
 * カウントダウンとして表示していたため、次の問題があった。
 *
 * - 閾値 14 を「日数」として扱っていた（確定仕様では **音声件数**）
 * - 28（STANDARD_DAYS_REQUIRED）は比率入力の後方互換の母数であり、表示用の日数ではない
 * - 結果として、1 日に複数回録音する利用者ほど実態より遠い数字が出ていた
 *
 * BE が 2026-09-11 に進捗フィールドを追加したため、実値で残量を表示する。
 * **`estimated_seconds_to_display` は BE が「先に到達する側」を判断済みの値**
 * （segment 経路と録音回数経路の両方を見積もり、短い方を返す）。
 * FE はしきい値（300 / 14）の解釈を持たず、この値をそのまま使う。
 * 同じしきい値を 2 箇所に持たせたことが #61 の不整合の原因だったため。
 *
 * BE が値を返さない場合（未デプロイ・旧データ等）は null のままとし、
 * 数量を断定しない表現にフォールバックする。誤った数字を出すより実害が小さい。
 */
import type { DailyReport, MCSResponse } from '../api/client';

export interface McsProgress {
  /** スコアを数値表示してよいか（standard / short_reference のみ） */
  showScore: boolean;
  /** 参考値扱いのバッジを出すか */
  isReference: boolean;
  /**
   * 表示到達までの残量。BE が値を返さない場合は null。
   * null の間は数量を断定しない案内文にフォールバックする。
   */
  remaining: {
    /** 目安の残り分数（切り上げ。1 分未満でも 0 にしない） */
    /** 30 秒単位に切り上げた残り時間（秒） */
    approxSeconds: number;
    /** 残り segment 件数（参考値。表示には使わないが将来の併記用に保持） */
    segments: number | null;
    /** 残り録音回数（同上） */
    recordings: number | null;
  } | null;
}

export function getMcsProgress(mcs: MCSResponse | null): McsProgress {
  const mode = mcs?.mode_applied ?? null;
  const available = mcs?.available === true && mcs.mcs_score != null;
  return {
    showScore: available && (mode === 'standard' || mode === 'short_reference'),
    isReference: mode === 'short_reference',
    remaining: buildRemaining(mcs),
  };
}

/**
 * BE の見積り秒数から表示用の残量を作る。
 * - 値が無い（未デプロイ・旧データ）→ null を返し、案内文にフォールバック
 * - 0 以下 → 条件を満たしているので null（カウントダウンを出さない）
 * - 端数は **30 秒単位で切り上げる**（課題 #61 で CENTRIC が確定）。
 *
 *   切り上げるのは、案内した分だけ話せば必ず条件に到達させるため。四捨五入だと
 *   67 秒が「約 1 分」となり、案内どおり 1 分話しても届かない。
 *   「言われたとおりにやったのに反応しない」は利用者にはアプリの故障に見える。
 *
 *   一方で 1 分単位の切り上げだと 67 秒が「約 2 分」になり、実態から離れる。
 *   30 秒単位にすると「約 1 分 30 秒」となり、切り上げの保証を保ったまま
 *   実態に近づく。BE も estimated_seconds_to_display を切り上げで返しているため、
 *   ここで四捨五入すると BE の切り上げを打ち消してしまう。
 *
 *   「あと約 0 秒」では案内にならないため最低 30 秒とする。
 */
function buildRemaining(mcs: MCSResponse | null): McsProgress['remaining'] {
  const seconds = mcs?.estimated_seconds_to_display;
  if (seconds == null || seconds <= 0) return null;
  return {
    approxSeconds: Math.max(30, Math.ceil(seconds / 30) * 30),
    segments: mcs?.segments_to_display ?? null,
    recordings: mcs?.recordings_to_display ?? null,
  };
}

/**
 * 残り時間（秒）を画面の文言用に「分」と「秒」へ分解する（課題 #61）。
 *
 * CENTRIC 確定の表記は「あと約 1 分 30 秒（目安）」。
 * **秒が 0 のときは秒を省く**（「あと約 2 分（目安）」）。
 * 「1 分半」を採らなかったのは、2 分 30 秒以降で表記が揃わないため。
 */
export function splitRemaining(approxSeconds: number): { minutes: number; seconds: number } {
  const total = Math.max(0, Math.round(approxSeconds));
  return { minutes: Math.floor(total / 60), seconds: total % 60 };
}

/**
 * カウントダウン文言のキーと値を返す（課題 #61）。
 *
 * ダッシュボードとコンディション画面の 2 箇所で同じ文言を出すため、
 * 分岐を 1 箇所にまとめる。片方だけ直して表記がずれるのを防ぐ。
 */
export function countdownLabel(
  approxSeconds: number
): { key: string; params: Record<string, number> } {
  const { minutes, seconds } = splitRemaining(approxSeconds);
  if (minutes === 0) {
    return { key: 'dashboard.monthlyMcs.countdownSecondsOnly', params: { seconds } };
  }
  if (seconds === 0) {
    return { key: 'dashboard.monthlyMcs.countdownMinutes', params: { minutes } };
  }
  return { key: 'dashboard.monthlyMcs.countdownMinutesSeconds', params: { minutes, seconds } };
}

/** 表示までの残りの状態（課題 #61） */
export type CountdownState =
  | { kind: 'remaining'; approxSeconds: number }
  | { kind: 'met' }
  | { kind: 'unknown' };

/** 残り秒数から状態を作る。丸め方は月次と同じ（30 秒単位の切り上げ） */
export function countdownStateFrom(seconds: number | null | undefined): CountdownState {
  if (seconds == null || !Number.isFinite(seconds)) return { kind: 'unknown' };
  if (seconds <= 0) return { kind: 'met' };
  const remaining = buildRemaining({ estimated_seconds_to_display: seconds } as MCSResponse);
  return remaining ? { kind: 'remaining', approxSeconds: remaining.approxSeconds } : { kind: 'unknown' };
}

/**
 * 画面に出す残りの状態（課題 #61。2026-09-29 高尾さん決定）。
 *
 * 日次レポートの mcs_progress（直近 28 日・録音のたびに更新）を使う。月次（前月分）の値は、
 * 今日いくら録音しても翌月まで変わらず、「あと何分話せば」の案内にならないため。
 * 当日のレポートがまだ無い（今日まだ録音していない）ときだけ、月次の値にとどめる。
 */
export function countdownForDisplay(
  report: Pick<DailyReport, 'mcsProgress'> | null | undefined,
  mcs: MCSResponse | null | undefined
): CountdownState {
  if (report?.mcsProgress) return countdownStateFrom(report.mcsProgress.estimated_seconds_to_display);
  return countdownStateFrom(mcs?.estimated_seconds_to_display);
}

/** 残りの状態に合う文言のキー。ダッシュボードとコンディション画面で同じものを使う */
export function countdownMessage(state: CountdownState): { key: string; params: Record<string, number> } {
  if (state.kind === 'remaining') return countdownLabel(state.approxSeconds);
  if (state.kind === 'met') return { key: 'dashboard.monthlyMcs.countdownMet', params: {} };
  return { key: 'dashboard.monthlyMcs.countdownUnknown', params: {} };
}
